import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const ticketService = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  getByNumber: (number) => api.get(`/tickets/number/${number}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  updateStatus: (id, statusId) => api.patch(`/tickets/${id}/status`, { statusId }),
  assign: (id, assigneeId) => api.patch(`/tickets/${id}/assign`, { assigneeId }),
  delete: (id) => api.delete(`/tickets/${id}`),
  getComments: (id, includeInternal) => api.get(`/tickets/${id}/comments`, { params: { includeInternal } }),
  addComment: (id, data) => api.post(`/tickets/${id}/comments`, data),
  getStatuses: () => api.get('/tickets/statuses'),
  getPriorities: () => api.get('/tickets/priorities'),
  getStats: (params) => api.get('/tickets/stats', { params }),
  getMyTickets: (params) => api.get('/tickets/my-tickets', { params }),
  getAssignedTickets: (params) => api.get('/tickets/assigned', { params }),
};

export const faqService = {
  getAll: (params) => api.get('/faqs', { params }),
  getById: (id) => api.get(`/faqs/${id}`),
  create: (data) => api.post('/faqs', data),
  update: (id, data) => api.put(`/faqs/${id}`, data),
  delete: (id) => api.delete(`/faqs/${id}`),
  search: (q, params) => api.get('/faqs/search', { params: { q, ...params } }),
  getPopular: (params) => api.get('/faqs/popular', { params }),
  getFeatured: (params) => api.get('/faqs/featured', { params }),
  recordFeedback: (id, isHelpful) => api.post(`/faqs/${id}/feedback`, { isHelpful }),
  getCategories: (params) => api.get('/faqs/categories', { params }),
  getCategoryById: (id) => api.get(`/faqs/categories/${id}`),
  getCategoryBySlug: (slug) => api.get(`/faqs/categories/slug/${slug}`),
  createCategory: (data) => api.post('/faqs/categories', data),
  updateCategory: (id, data) => api.put(`/faqs/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/faqs/categories/${id}`),
};

export const hotelService = {
  getAll: (params) => api.get('/hotels', { params }),
  getById: (id) => api.get(`/hotels/${id}`),
  getByCode: (code) => api.get(`/hotels/code/${code}`),
  create: (data) => api.post('/hotels', data),
  update: (id, data) => api.put(`/hotels/${id}`, data),
  delete: (id) => api.delete(`/hotels/${id}`),
  getStats: (id) => api.get(`/hotels/${id}/stats`),
};

export const teamService = {
  getAll: (params) => api.get('/teams', { params }),
  getById: (id) => api.get(`/teams/${id}`),
  getByCode: (code) => api.get(`/teams/code/${code}`),
  getByHotel: (hotelId) => api.get(`/teams/hotel/${hotelId}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  getMembers: (id) => api.get(`/teams/${id}/members`),
  getStats: (id) => api.get(`/teams/${id}/stats`),
};

export const userService = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  assignHotel: (id, hotelId, isPrimary) => api.post(`/users/${id}/hotels`, { hotelId, isPrimary }),
  removeHotel: (id, hotelId) => api.delete(`/users/${id}/hotels/${hotelId}`),
  assignTeam: (id, teamId, isLead) => api.post(`/users/${id}/teams`, { teamId, isLead }),
  removeTeam: (id, teamId) => api.delete(`/users/${id}/teams/${teamId}`),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  getRoles: () => api.get('/users/roles'),
};

export const uploadService = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFiles: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/uploads/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteFile: (type, filename) => api.delete(`/uploads/${type}/${filename}`),
};
